import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/register.css';
import { buildApiUrl } from '../../config/apiConfig';
import { useNotifications } from '../../context/NotificationContext';
import { logError } from '../../utils/logger';

const initialFormState = {
  nombre: '',
  apellido: '',
  email: '',
  password: '',
  confirmPassword: '',
  aceptoTerminos: false,
  aceptoNewsletter: false,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const evaluatePasswordStrength = (password) => {
  if (!password) {
    return { label: 'Ingresa una contraseña', className: '' };
  }

  let strength = 0;
  if (password.length >= 6) strength += 1;
  if (password.length >= 8) strength += 1;
  if (/[a-z]/.test(password)) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password) || /[^A-Za-z]/.test(password)) strength += 1;

  if (strength <= 1) {
    return { label: 'Muy débil', className: 'strength-weak' };
  }

  if (strength === 2) {
    return { label: 'Débil', className: 'strength-fair' };
  }

  if (strength === 3) {
    return { label: 'Buena', className: 'strength-good' };
  }

  return { label: 'Muy fuerte', className: 'strength-strong' };
};

const RegisterPage = () => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(evaluatePasswordStrength(''));

  const { notify } = useNotifications();
  const navigate = useNavigate();

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setPasswordStrength(evaluatePasswordStrength(value));
    }

    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleCheckboxChange = useCallback((event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const validate = useCallback(() => {
    const validationErrors = {};

    if (!form.nombre.trim()) {
      validationErrors.nombre = 'El nombre es obligatorio';
    }

    if (!form.apellido.trim()) {
      validationErrors.apellido = 'El apellido es obligatorio';
    }

    if (!form.email.trim()) {
      validationErrors.email = 'El correo es obligatorio';
    } else if (!emailRegex.test(form.email.trim())) {
      validationErrors.email = 'Ingresa un correo válido';
    }

    if (!form.password) {
      validationErrors.password = 'La contraseña es obligatoria';
    } else if (form.password.length < 6) {
      validationErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!form.confirmPassword) {
      validationErrors.confirmPassword = 'Debes confirmar la contraseña';
    } else if (form.password !== form.confirmPassword) {
      validationErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return validationErrors;
  }, [form.apellido, form.confirmPassword, form.email, form.nombre, form.password]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setMessage(null);

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      if (!form.aceptoTerminos) {
        setMessage({
          type: 'error',
          text: 'Debes aceptar los términos y condiciones para continuar.',
        });
        return;
      }

      setErrors({});
      setLoading(true);

      try {
        const response = await fetch(buildApiUrl('/api/user'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nombre: form.nombre.trim(),
            apellido: form.apellido.trim(),
            email: form.email.trim(),
            password: form.password,
            aceptoTerminos: true,
            suscritoNewsletter: form.aceptoNewsletter,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setMessage({
            type: 'success',
            text: '¡Cuenta creada exitosamente! Ahora puedes iniciar sesión.',
          });
          notify({ message: 'Cuenta creada correctamente. Revisa tu correo para más detalles.', type: 'success' });
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1800);
        } else {
          const errorText = data?.error || 'Error al crear la cuenta. Inténtalo nuevamente.';
          setMessage({ type: 'error', text: errorText });
        }
      } catch (error) {
        logError('[RegisterPage] Error registrando usuario', error);
        setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
      } finally {
        setLoading(false);
      }
    },
    [
      form.aceptoNewsletter,
      form.aceptoTerminos,
      form.apellido,
      form.email,
      form.nombre,
      form.password,
      navigate,
      notify,
      validate,
    ],
  );

  const handleViewTerms = useCallback(() => {
    notify({
      message:
        'Los términos y condiciones se encuentran en actualización. Contacta a soporte para obtener una copia vigente.',
      type: 'info',
    });
  }, [notify]);

  const handleViewPrivacy = useCallback(() => {
    notify({
      message:
        'La política de privacidad estará disponible pronto. Mientras tanto, escríbenos para conocer cómo protegemos tus datos.',
      type: 'info',
    });
  }, [notify]);

  return (
    <div className="register-page" role="main">
      <div className="register-container">
        <div className="register-form-section">
          <div className="register-form-header">
            <h2>Crear Cuenta</h2>
            <p>Completa tus datos para comenzar tu aventura educativa</p>
          </div>

          <form className="register-form" onSubmit={handleSubmit} noValidate>
            {message ? (
              <div
                className={`auth-message ${message.type === 'error' ? 'error' : 'success'}`}
                role="alert"
              >
                {message.text}
              </div>
            ) : null}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="registerNombre" className="form-label">
                  <i className="fas fa-user" aria-hidden="true" />
                  Nombre
                </label>
                <input
                  type="text"
                  id="registerNombre"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleInputChange}
                  className={`form-input${errors.nombre ? ' error' : ''}`}
                  placeholder="Tu nombre"
                  autoComplete="given-name"
                  required
                />
                {errors.nombre ? <div className="form-error">{errors.nombre}</div> : null}
              </div>

              <div className="form-group">
                <label htmlFor="registerApellido" className="form-label">
                  <i className="fas fa-user" aria-hidden="true" />
                  Apellido
                </label>
                <input
                  type="text"
                  id="registerApellido"
                  name="apellido"
                  value={form.apellido}
                  onChange={handleInputChange}
                  className={`form-input${errors.apellido ? ' error' : ''}`}
                  placeholder="Tu apellido"
                  autoComplete="family-name"
                  required
                />
                {errors.apellido ? <div className="form-error">{errors.apellido}</div> : null}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="registerEmail" className="form-label">
                <i className="fas fa-envelope" aria-hidden="true" />
                Correo Electrónico
              </label>
              <input
                type="email"
                id="registerEmail"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                className={`form-input${errors.email ? ' error' : ''}`}
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
              {errors.email ? <div className="form-error">{errors.email}</div> : null}
            </div>

            <div className="form-group">
              <label htmlFor="registerPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Contraseña
              </label>
              <input
                type="password"
                id="registerPassword"
                name="password"
                value={form.password}
                onChange={handleInputChange}
                className={`form-input${errors.password ? ' error' : ''}`}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
                required
              />
              <div className="password-strength" aria-live="polite">
                <div className="strength-bar">
                  <div className={`strength-fill ${passwordStrength.className}`} />
                </div>
                <span>{passwordStrength.label}</span>
              </div>
              {errors.password ? <div className="form-error">{errors.password}</div> : null}
            </div>

            <div className="form-group">
              <label htmlFor="registerConfirmPassword" className="form-label">
                <i className="fas fa-check-circle" aria-hidden="true" />
                Confirmar Contraseña
              </label>
              <input
                type="password"
                id="registerConfirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleInputChange}
                className={`form-input${errors.confirmPassword ? ' error' : ''}`}
                placeholder="Repite tu contraseña"
                autoComplete="new-password"
                required
              />
              {errors.confirmPassword ? (
                <div className="form-error">{errors.confirmPassword}</div>
              ) : null}
            </div>

            <div className="terms-checkbox">
              <input
                type="checkbox"
                id="aceptoTerminos"
                name="aceptoTerminos"
                checked={form.aceptoTerminos}
                onChange={handleCheckboxChange}
                required
              />
              <label htmlFor="aceptoTerminos">
                Acepto los{' '}
                <button type="button" onClick={handleViewTerms}>
                  Términos y Condiciones
                </button>{' '}
                y la{' '}
                <button type="button" onClick={handleViewPrivacy}>
                  Política de Privacidad
                </button>
              </label>
            </div>

            <div className="terms-checkbox">
              <input
                type="checkbox"
                id="aceptoNewsletter"
                name="aceptoNewsletter"
                checked={form.aceptoNewsletter}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="aceptoNewsletter">
                Quiero recibir noticias y actualizaciones sobre cursos y eventos
              </label>
            </div>

            <button type="submit" className="register-submit" disabled={loading || hasErrors}>
              <span
                className="auth-loading"
                aria-hidden="true"
                style={{ display: loading ? 'inline-block' : 'none' }}
              />
              <i className="fas fa-user-plus" aria-hidden="true" />
              Crear Mi Cuenta
            </button>
          </form>

          <div className="login-link">
            <p>¿Ya tienes una cuenta?</p>
            <Link to="/login">Inicia sesión aquí</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;

